import { Link } from 'react-router-dom';

import './TermsPage.css';

/**
 * Terms of Service.
 *
 * Static, and deliberately so. A legal document that renders from data is a legal document
 * that can change without anyone reviewing the change, so this is written out in full and
 * moves only when someone edits this file.
 *
 * THE PARTY HERE IS VYRTUAL LLC, not Voleto. Voleto is the platform; the Company that
 * contracts with users, sells tickets and bears the obligations below is Vyrtual LLC, and
 * the copy names it throughout rather than blurring the two.
 *
 * Section 5 is the one linked from checkout, so it carries an id and the fee figures match
 * the constants settlement actually applies.
 */
export function TermsPage() {
  return (
    <div className="legal">
      <header className="legal__head">
        <h1 className="legal__title display">Terms of Service</h1>
        <dl className="legal__dates">
          <div>
            <dt>Effective date</dt>
            <dd className="mono">January 4, 2022</dd>
          </div>
          <div>
            <dt>Last modified</dt>
            <dd className="mono">August 25, 2026</dd>
          </div>
        </dl>
      </header>

      <section className="legal__section">
        <h2>1. Description of Service</h2>
        <p>
          These Terms of Service govern your use of online media, ticketing, content
          distribution, and platform services provided by Vyrtual LLC (the &ldquo;Company&rdquo;,
          &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) through its websites,
          applications, and technology infrastructure (collectively, the &ldquo;Services&rdquo;).
          These Terms apply to all users, ticket buyers, and platform participants.
        </p>
      </section>

      <section className="legal__section">
        <h2>2. Acceptance and Changes to Terms</h2>
        <p>
          The Company reserves the right, at its sole discretion, to modify, add, or remove
          portions of these Terms at any time without prior notice. Any changes will be
          effective immediately upon posting. Your continued use of the Services following
          modifications constitutes your acceptance of the revised Terms.
        </p>
      </section>

      <section className="legal__section">
        <h2>3. Access and Eligibility</h2>
        <p>
          Users accessing the Services must be at least thirteen (13) years of age. Users
          registering accounts, completing financial transactions, or purchasing tickets
          must be at least eighteen (18) years of age. You are solely responsible for
          ensuring compliance with all local, state, and national laws in your jurisdiction
          when using the Services.
        </p>
      </section>

      <section className="legal__section">
        <h2>4. User Conduct and Security</h2>
        <p>The Services may only be used for lawful purposes. You agree not to:</p>
        <ul>
          <li>
            Access data, servers, or accounts without authorization, or test the
            vulnerability of platform systems.
          </li>
          <li>
            Interfere with service delivery via flooding, spamming, mailbombing, or
            uploading malicious code.
          </li>
          <li>
            Scrape, crawl, or harvest data, or use automated bots to skew transactions or
            payouts.
          </li>
          <li>
            Engage in harassment, hate speech, abusive behavior, or unauthorized commercial
            solicitation.
          </li>
        </ul>
        <p>
          Violations of system or network security may result in immediate account
          suspension and civil or criminal liability.
        </p>
      </section>

      {/* Linked directly from checkout, so it has a stable anchor. */}
      <section className="legal__section" id="fees">
        <h2>5. Ticketing, Pricing, and Non-Refundable Fees</h2>
        <ul>
          <li>
            <strong>All-Inclusive Pricing:</strong> All ticket purchases operate on an
            inclusive pricing model (&ldquo;what you see is what you pay&rdquo;). The total
            amount displayed at checkout reflects the full price.
          </li>
          <li>
            <strong>Platform and Processing Fees:</strong> Ticket transactions include a
            Company platform service fee (5.00%) and payment processing charges (2.90% +
            $0.30 buffer). These fees are absorbed into the ticket price and are strictly
            non-refundable under all circumstances, including buyer refund requests, event
            postponements, or user cancellations.
          </li>
          <li>
            <strong>Event Cancellations by Organizer:</strong> If an event is cancelled by
            the event organizer, ticket holders will receive a full refund of the ticket
            face value in accordance with platform settlement rules, with unrecoverable
            fees absorbed by the organizer.
          </li>
          <li>
            <strong>Event Postponements:</strong> If an event is postponed or rescheduled,
            tickets remain valid for the new date. Ticket holders who cannot attend the
            rescheduled date may request a refund of the ticket purchase price, minus
            non-refundable platform and processing fees, within thirty (30) days of the
            postponement announcement.
          </li>
          <li>
            <strong>Buyer-Initiated Refunds:</strong> For active, non-cancelled events where
            the organizer permits refunds, approved buyer refunds will be processed minus
            all non-refundable platform service fees and credit card processing charges.
          </li>
        </ul>
      </section>

      <section className="legal__section">
        <h2>6. Intellectual Property</h2>
        <p>
          All content, binaries, interface designs, layout, text, graphics, documentation,
          and underlying software associated with the Services are the exclusive property of
          Vyrtual LLC and its licensors, protected by United States and international
          intellectual property laws. You may not copy, reverse engineer, decompile, modify,
          distribute, or create derivative works of any platform software.
        </p>
      </section>

      <section className="legal__section">
        <h2>7. Disclaimer of Warranties</h2>
        <p className="legal__caps">
          THE SERVICES ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo;,
          WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT
          LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, AND NON-INFRINGEMENT. THE COMPANY DOES NOT WARRANT THAT SERVICE OPERATION
          WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
        </p>
      </section>

      <section className="legal__section">
        <h2>8. Limitation of Liability</h2>
        <p className="legal__caps">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER THE COMPANY NOR ITS DIRECTORS,
          OFFICERS, EMPLOYEES, AFFILIATES, OR AGENTS SHALL BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED
          TO YOUR USE OF THE SERVICES, TICKETING TRANSACTIONS, OR ATTENDANCE AT ANY EVENT.
          THE COMPANY&rsquo;S MAXIMUM AGGREGATE LIABILITY SHALL NOT EXCEED THE ACTUAL AMOUNT
          PAID BY YOU FOR THE SPECIFIC SERVICE OR TICKET IN DISPUTE.
        </p>
      </section>

      <section className="legal__section">
        <h2>9. Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless the Company and its affiliates
          from and against any claims, liabilities, damages, losses, or expenses (including
          reasonable legal fees) arising out of your use of the Services or violation of
          these Terms.
        </p>
      </section>

      <section className="legal__section">
        <h2>10. Governing Law and Jurisdiction</h2>
        <p>
          These Terms of Service shall be governed by and construed in accordance with the
          laws of the State of Colorado, without regard to conflict of law principles. You
          consent to the exclusive jurisdiction of the state and federal courts located in
          Colorado for any disputes arising under these Terms.
        </p>
      </section>

      <footer className="legal__foot">
        <Link className="legal__back" to="/">Back to Voleto</Link>
      </footer>
    </div>
  );
}
